import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import './AuthLoginModalContent.css';
import { BASE_URL } from '../../../../config';
import { client } from '../../../../api/client';
import { setAuthSession, clearAuthToken } from '../../../../auth/token';

function AuthLoginModalContent({ onClose }) {
  const [step, setStep] = useState('credentials'); // credentials | organizations | success
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const isDisabled = useMemo(() => isLoading || !email || !password, [email, password, isLoading]);

  const handleLogin = async (organizationId = null) => {
    setIsLoading(true);
    setError(null);

    try {
      const payload = await client.post(`${BASE_URL}/auth/login`, {
        email,
        password,
        organization_id: organizationId,
      });

      if (payload.requiresOrganizationSelection && payload.organizations) {
        setOrganizations(payload.organizations);
        setStep('organizations');
        return;
      }

      if (payload.token) {
        setAuthSession(payload);
      } else {
        clearAuthToken();
      }

      setStep('success');
      window.location.reload();
    } catch (err) {
      if (err?.data?.organizations && err?.data?.requiresOrganizationSelection) {
        setOrganizations(err.data.organizations);
        setStep('organizations');
        return;
      }

      const message = err?.message || 'No pudimos iniciar sesión. Intenta nuevamente.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    handleLogin();
  };

  const handleSelectOrganization = (orgId) => {
    handleLogin(orgId);
  };

  const renderBaseLayout = (content) => (
    <div className="auth-login-screen">
      <div className="auth-login-shell">
        <div className="auth-login-content">{content}</div>
      </div>
    </div>
  );

  const renderCredentialStep = () => (
    renderBaseLayout(
      <form className="auth-login-form" onSubmit={handleSubmit}>
        <div className="auth-login-brand">
          <img className="auth-login-logo" src="/logo-blue-sololine.png" alt="Sololine" />
        </div>
        <h2 className="auth-login-title">Inicia sesión</h2>
        <label className="auth-login-label">
          <span className="auth-login-label-text">Correo electrónico</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
        </label>
        <label className="auth-login-label">
          <span className="auth-login-label-text">Contraseña</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
          />
        </label>
        {error && <p className="auth-login-error">{error}</p>}
        <div className="auth-login-actions">
          <button type="submit" className="auth-login-button primary" disabled={isDisabled}>
            {isLoading ? 'Entrando...' : 'Iniciar sesión'}
          </button>
        </div>
        <div className="auth-login-link-row">
          <a className="auth-login-link" href="#">
            ¿Olvidaste tu contraseña?
          </a>
        </div>
        <div className="auth-login-separator" />
        <div className="auth-login-actions">
          <button type="button" className="auth-login-button create-account" disabled>
            Crear cuenta nueva
          </button>
        </div>
      </form>,
    )
  );

  const renderOrgStep = () => (
    renderBaseLayout(
      <div className="auth-login-form">
        <div className="auth-login-brand">
          <img className="auth-login-logo" src="/logo-blue-sololine.png" alt="Sololine" />
        </div>
        <h2 className="auth-login-title">Selecciona una cuenta</h2>
        <p className="auth-login-subtitle">
          Encontramos varias organizaciones asociadas a {email}. Elige con cuál deseas entrar.
        </p>
        <div className="auth-login-org-list">
          {organizations.map((org) => (
            <button
              key={org.organization_id}
              type="button"
              className="auth-login-org"
              onClick={() => handleSelectOrganization(org.organization_id)}
              disabled={isLoading}
            >
              <span className="auth-login-org__id">Org #{org.organization_id}</span>
              <span className="auth-login-org__admin">Admin: {org.admin_email || 'N/D'}</span>
              <span className="auth-login-org__status">{org.subscription_status}</span>
            </button>
          ))}
        </div>
        {error && <p className="auth-login-error">{error}</p>}
        <div className="auth-login-actions">
          <button type="button" className="auth-login-button secondary" onClick={onClose}>
            Cancelar
          </button>
        </div>
      </div>,
    )
  );

  if (step === 'organizations') {
    return renderOrgStep();
  }

  if (step === 'success') {
    return (
      renderBaseLayout(
        <div className="auth-login-form">
          <div className="auth-login-brand">
            <img className="auth-login-logo" src="/logo-blue-sololine.png" alt="Sololine" />
          </div>
          <h2 className="auth-login-title">¡Listo!</h2>
          <p className="auth-login-subtitle">Refrescando para continuar...</p>
        </div>,
      )
    );
  }

  return renderCredentialStep();
}

AuthLoginModalContent.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default AuthLoginModalContent;
